import React, { useState } from "react";
import axios from "axios";
import {
  ShieldCheck, Search, Loader2, CheckCircle2, XCircle, Award, CalendarDays, Clock, Camera,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

export default function VerificaAttestato() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const verifica = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.get(`${API}/api/formazione/verifica-attestato/${encodeURIComponent(code.trim())}`);
      setResult(r.data);
    } catch (_e) {
      setResult({ valido: false, messaggio: "Errore di verifica, riprova." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7ff]">
      <SEO
        title="Verifica Attestato — A.C.D.T."
        description="Verifica l'autenticità di un attestato di partecipazione al percorso formativo A.C.D.T. tramite l'identificativo univoco."
      />
      <Navbar />

      <section className="bg-[#0a1f55] text-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[11.5px] font-extrabold uppercase tracking-wider">
            <ShieldCheck className="h-3.5 w-3.5" /> Registro attestati
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold leading-tight">Verifica un attestato A.C.D.T.</h1>
          <p className="mt-3 text-slate-300 text-sm md:text-base max-w-xl mx-auto">
            Inserisci l'<strong>ID ATTESTATO</strong> riportato in fondo al documento (es. ACDT-ATT-1A2B3C4D)
            per verificarne l'autenticità nel registro ufficiale dell'Associazione.
          </p>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-5 -mt-8 pb-20">
        <form onSubmit={verifica} className="rounded-2xl bg-white border border-slate-200 shadow-xl p-5 flex flex-col sm:flex-row gap-3" data-testid="verifica-attestato-form">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="ID attestato — es. ACDT-ATT-1A2B3C4D"
            data-testid="verifica-attestato-input"
            className="h-12 flex-1 text-[15px]"
          />
          <Button type="submit" disabled={loading || !code.trim()} data-testid="verifica-attestato-btn" className="h-12 px-6 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-2 h-4 w-4" /> Verifica</>}
          </Button>
        </form>

        {result && !result.valido && (
          <div className="mt-6 rounded-2xl border-2 border-red-200 bg-red-50 p-6 flex items-start gap-3" data-testid="verifica-esito-negativo">
            <XCircle className="h-6 w-6 text-red-600 shrink-0" />
            <div>
              <h3 className="font-extrabold text-red-700">Attestato non trovato</h3>
              <p className="text-[13.5px] text-red-600 mt-1">{result.messaggio || "Nessun attestato corrisponde all'identificativo inserito. Controlla il codice e riprova."}</p>
            </div>
          </div>
        )}

        {result && result.valido && (
          <div className="mt-6 rounded-2xl border-2 border-emerald-300 bg-white overflow-hidden" data-testid="verifica-esito-positivo">
            <div className="bg-emerald-50 border-b border-emerald-200 p-5 flex items-center gap-3">
              <CheckCircle2 className="h-7 w-7 text-emerald-600 shrink-0" />
              <div>
                <h3 className="font-extrabold text-emerald-700 text-lg">Attestato autentico ✓</h3>
                <p className="text-[12.5px] text-emerald-600">Registrato nel registro ufficiale A.C.D.T. — {result.attestato_id}</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#0a1f55] text-[#f59e0b] flex items-center justify-center"><Award className="h-5 w-5" /></div>
                <div>
                  <div className="font-extrabold text-[#0a1f55] text-lg" data-testid="verifica-nome">{result.nome} {result.cognome}</div>
                  <div className="text-[12.5px] text-slate-500">Tessera A.C.D.T. n. {result.numero_tessera || "—"}</div>
                </div>
              </div>
              <div className="rounded-xl bg-[#f5f7ff] p-4 text-[13px] text-slate-700 font-semibold">{result.corso}</div>
              <div className="grid sm:grid-cols-3 gap-3">
                {[
                  { icon: CheckCircle2, l: "Moduli completati", v: result.moduli_completati },
                  { icon: Clock, l: "Ore tracciate", v: `${result.ore} h` },
                  { icon: Camera, l: "Presenza media", v: `${result.presenza_media}%` },
                ].map((s) => (
                  <div key={s.l} className="rounded-xl border border-slate-200 p-3.5">
                    <s.icon className="h-4 w-4 text-[#102a6b]" />
                    <div className="mt-1.5 text-[10.5px] uppercase tracking-wider font-bold text-slate-500">{s.l}</div>
                    <div className="font-extrabold text-[#0a1f55]">{s.v}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12.5px] text-slate-500">
                <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Periodo: {fmtDate(result.periodo_da)} — {fmtDate(result.periodo_a)}</span>
                <span>Rilasciato il: {fmtDate(result.generated_at)}</span>
                <span>Codice verifica: <strong className="text-[#0a1f55]">{result.codice_verifica}</strong></span>
              </div>
            </div>
          </div>
        )}

        <p className="mt-8 text-center text-[12px] text-slate-500">
          La verifica interroga in tempo reale il registro attestati della piattaforma A.C.D.T.<br />
          Per assistenza: <a href="mailto:info@assoacdt.com" className="text-[#102a6b] font-bold underline">info@assoacdt.com</a>
        </p>
      </section>

      <Footer />
    </div>
  );
}
